import * as Sentry from "@sentry/react";
import { createBrowserRouter, Outlet, type RouteObject } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { initializeSentry } from "@/external/sentry";
import { askNobiRoute } from "@/modules/ask-nobi/router";
import { authenticationRoute } from "@/modules/authentication/router";
import { onboardingFlowRoute } from "@/modules/onboarding-flow/router";
import { quickReplyRoute } from "@/modules/quick-reply/pages/router";
import { ChatsRedirect } from "@/modules/redirects/ChatsRedirect";
import { PortfolioRedirect } from "@/modules/redirects/PortfolioRedirect";
import { RedirectToRoute } from "@/modules/redirects/RedirectToRoute";
import { ShareRedirect } from "@/modules/redirects/ShareRedirect";
import { registrationRoute } from "@/modules/registration/router";
import { routes } from "@/routes";

import { AskNobiLayout, StandardPageLayout } from "./AppLayouts";
import { GlobalProviders } from "./AppProviders";
import {
  AuthenticatedRouteShell,
  AuthenticationRouteShell,
  QuickReplyRouteShell,
  RegistrationRouteShell,
} from "./AppRouteShells";
import { SuperTokensProvider } from "./authentication/SuperTokensProvider";
import { protectedRoutes } from "./router";

initializeSentry();
const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

export const router = sentryCreateBrowserRouter(
  [
    {
      errorElement: <ErrorPage />,
      element: (
        <GlobalProviders>
          <Outlet />
        </GlobalProviders>
      ),
      children: [
        {
          path: routes.quickReply.ROOT,
          element: <QuickReplyRouteShell />,
          children: quickReplyRoute,
        },
        {
          path: "/",
          element: (
            <SuperTokensProvider>
              <Outlet />
            </SuperTokensProvider>
          ),
          children: [
            {
              path: routes.authentication.ROOT,
              element: <AuthenticationRouteShell />,
              children: authenticationRoute,
            },
            {
              path: routes.registration.ROOT,
              element: <RegistrationRouteShell />,
              children: registrationRoute,
            },
            {
              element: <AuthenticatedRouteShell />,
              children: [
                { path: "/", element: <PortfolioRedirect /> },
                { path: "/@", element: <PortfolioRedirect /> },
                { path: "/share", element: <ShareRedirect /> },
                { path: "/chats", element: <ChatsRedirect /> },
                {
                  path: routes.askNobiChat.ROOT,
                  element: <AskNobiLayout />,
                  children: askNobiRoute,
                },
                {
                  path: routes.onboardingFlow.ROOT,
                  element: <Outlet />,
                  children: onboardingFlowRoute,
                },
                {
                  element: <StandardPageLayout />,
                  children: protectedRoutes,
                },
              ],
            },
            { path: "/login", element: <RedirectToRoute to={routes.authentication.login} /> },
            { path: "/logout", element: <RedirectToRoute to={routes.authentication.logout} /> },
          ],
        },
      ],
    },
  ] satisfies RouteObject[],
  {
    future: {
      v7_relativeSplatPath: true,
      v7_startTransition: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  },
);
